import React from 'react'
import {
	EuiFlexGroup,
	EuiFlexItem,
	EuiButton,
	EuiText,
	EuiIcon,
} from '@equipmentshare/ds2'
import { AddLineIcon, FileTextLineIcon } from '@equipmentshare/ds2-icons'
import { heapEvent } from '@equipmentshare/heaper'
import LineItemForm from './LineItemForm'
import {
	DemandRequestLineItem,
	requiresTargetId,
	TARGET_ID_ERROR_MESSAGES,
	hasUnsatisfiedBusinessReason,
	NEEDS_BUSINESS_REASON_ERROR,
} from '../hooks/useDemandRequestForm'
import { COLUMN_WIDTHS } from '../constants/columnWidths'

export type LineItemsListProps = {
	/** Array of line items */
	lineItems: DemandRequestLineItem[],
	/** Callback to add a new line item */
	onAddLineItem: () => void,
	/** Callback to remove a line item at specific index */
	onRemoveLineItem: ( index: number ) => void,
	/** Callback to update a line item at specific index */
	onUpdateLineItem: ( index: number, updates: Partial<DemandRequestLineItem> ) => void,
	/** Whether there are validation errors for line items */
	hasValidationErrors?: boolean,
	/** General error message for line items */
	validationErrorMessage?: string,
	/** Whether to show validation errors (only after form interaction or submit attempt) */
	showValidationErrors?: boolean,
	/** Branch ID to filter work orders and invoices by */
	branchId?: string,
}

export default function LineItemsList( {
	lineItems,
	onAddLineItem,
	onRemoveLineItem,
	onUpdateLineItem,
	hasValidationErrors,
	validationErrorMessage,
	showValidationErrors = false,
	branchId,
}: LineItemsListProps ) {
	const handleAddLineItem = () => {
		heapEvent( 'Inventory - Stock Request - Page - Click - Add Line Item', {} )
		onAddLineItem()
	}

	const handleRemoveLineItem = ( index: number ) => {
		heapEvent( 'Inventory - Stock Request - Page - Click - Remove Line Item', {
			line_item_index: index,
		} )
		onRemoveLineItem( index )
	}

	const handleUpdateLineItem = ( index: number, updates: Partial<DemandRequestLineItem> ) => {
		onUpdateLineItem( index, updates )
	}

	// Check if individual line items have errors
	const getLineItemErrors = ( lineItem: DemandRequestLineItem, index: number ) => {
		const errors: string[] = []

		if ( !lineItem.productId ) {
			errors.push( 'Product is required' )
		}
		if ( !lineItem.quantity || lineItem.quantity < 1 ) {
			errors.push( 'Quantity must be at least 1' )
		}
		if ( lineItem.notes && lineItem.notes.length > 1000 ) {
			errors.push( 'Line notes cannot exceed 1000 characters' )
		}
		if ( hasUnsatisfiedBusinessReason( lineItem ) ) {
			errors.push( NEEDS_BUSINESS_REASON_ERROR )
		} else if ( requiresTargetId( lineItem.targetType ) && !lineItem.targetId ) {
			errors.push( TARGET_ID_ERROR_MESSAGES[ lineItem.targetType as 'WORK_ORDER' | 'INVOICE' | 'ORDER' ] )
		}

		return {
			hasErrors: showValidationErrors && errors.length > 0,
			errorMessage: showValidationErrors && errors.length > 0 ? `Line Item ${index + 1}: ${errors.join( ', ' )}` : undefined,
		}
	}

	return (
		<EuiFlexGroup direction="column" gutterSize="m">
			{/* Section Title */}
			<EuiFlexItem grow={false}>
				<EuiFlexGroup gutterSize="s" alignItems="center">
					<EuiFlexItem grow={false}>
						<EuiIcon type={FileTextLineIcon} size="m" />
					</EuiFlexItem>
					<EuiFlexItem grow={false}>
						<strong>Items</strong>
					</EuiFlexItem>
				</EuiFlexGroup>
			</EuiFlexItem>

			{/* General validation error */}
			{showValidationErrors && hasValidationErrors && validationErrorMessage && (
				<EuiFlexItem grow={false}>
					<EuiText color="danger">
						<strong>Error:</strong> {validationErrorMessage}
					</EuiText>
				</EuiFlexItem>
			)}

			{/* Immediate business-reason/WO/Invoice/Order error (shown as soon as the
			    line item is missing what it needs, without waiting for submit) */}
			{lineItems.map( ( item, index ) => {
				const needsBusinessReason = hasUnsatisfiedBusinessReason( item )
				const missingTargetId = requiresTargetId( item.targetType ) && !item.targetId

				if ( !needsBusinessReason && !missingTargetId ) {
					return null
				}
				const message = needsBusinessReason
					? NEEDS_BUSINESS_REASON_ERROR
					: TARGET_ID_ERROR_MESSAGES[ item.targetType as 'WORK_ORDER' | 'INVOICE' | 'ORDER' ]
				return (
					<EuiFlexItem key={`target-error-${item.id ?? index}`} grow={false}>
						<EuiText color="danger">
							<strong>Error:</strong> Line Item {index + 1}: {message}
						</EuiText>
					</EuiFlexItem>
				)
			} )}

			{/* Column Headers */}
			<EuiFlexItem grow={false}>
				<EuiFlexGroup direction="row" gutterSize="s" alignItems="center" wrap={false}>
					<EuiFlexItem>
						<EuiText size="xs" style={{ fontWeight: 600, textAlign: 'left' }}>Item</EuiText>
					</EuiFlexItem>
					<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.quantity }}>
						<EuiText size="xs" style={{ fontWeight: 600, textAlign: 'left' }}>Qty</EuiText>
					</EuiFlexItem>
					<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.needBy }}>
						<EuiText size="xs" style={{ fontWeight: 600, textAlign: 'left' }}>Need by</EuiText>
					</EuiFlexItem>
					<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.allowSubs }}>
						<EuiText size="xs" style={{ fontWeight: 600, textAlign: 'left' }}>Allow Subs</EuiText>
					</EuiFlexItem>
					<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.willCall }}>
						<EuiText size="xs" style={{ fontWeight: 600, textAlign: 'left' }}>Will Call</EuiText>
					</EuiFlexItem>
					<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.itemIsFor }}>
						<EuiText size="xs" style={{ fontWeight: 600, textAlign: 'left' }}>Item is for</EuiText>
					</EuiFlexItem>
					<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.noteButton }}>
						{/* Space for Add Note button */}
					</EuiFlexItem>
					<EuiFlexItem grow={false} style={{ width: COLUMN_WIDTHS.deleteButton }}>
						{/* Space for Delete button */}
					</EuiFlexItem>
				</EuiFlexGroup>
			</EuiFlexItem>

			{/* Line Items */}
			<EuiFlexItem>
				<EuiFlexGroup direction="column" gutterSize="s">
					{lineItems.map( ( lineItem, index ) => {
						const { hasErrors, errorMessage } = getLineItemErrors( lineItem, index )
						return (
							<EuiFlexItem key={lineItem.id ?? index}>
								<LineItemForm
									lineItem={lineItem}
									index={index}
									onUpdate={handleUpdateLineItem}
									onRemove={handleRemoveLineItem}
									isOnlyItem={lineItems.length === 1}
									showValidationErrors={showValidationErrors}
									hasErrors={hasErrors}
									errorMessage={errorMessage}
									branchId={branchId}
								/>
							</EuiFlexItem>
						)
					} )}
				</EuiFlexGroup>
			</EuiFlexItem>

			{/* Add Item Button - aligned with line item structure */}
			<EuiFlexItem grow={false}>
				<EuiFlexGroup direction="row" gutterSize="s" wrap={false}>
					<EuiFlexItem grow={false}>
						<EuiButton
							iconType={AddLineIcon}
							onClick={handleAddLineItem}
							color="primary"
							fill={false}
							data-testid="add-line-item-button"
						>
							Add Item
						</EuiButton>
					</EuiFlexItem>
				</EuiFlexGroup>
			</EuiFlexItem>

			{/* Minimum requirement info */}
			{lineItems.length === 0 && (
				<EuiFlexItem grow={false}>
					<EuiText size="s" color="subdued">
						At least one line item is required.
					</EuiText>
				</EuiFlexItem>
			)}
		</EuiFlexGroup>
	)
}
